import { useCallback, useRef } from 'react'

import { EditorEvents } from '@tiptap/core'
import { Node } from '@tiptap/pm/model'
import { EditorState } from '@tiptap/pm/state'

import { useUploadQueue } from '../../Providers/index.js'

// Detect when an image or attachment node is removed from editor while the upload is in progress.
// It relies on having 'uploadId' attribute on the node
export const useUploadInProgressRemovedListener = () => {
  const { removeFromUploadQueue } = useUploadQueue()
  const onNodeRemoved = useCallback(
    (node: Node) => {
      const { uploadId } = node.attrs
      if (uploadId) {
        removeFromUploadQueue({ id: uploadId })
      }
    },
    [removeFromUploadQueue],
  )

  // Remember the previous state, so we can diff edits and detect deleted nodes.
  const previousState = useRef<EditorState>()

  // from https://github.com/ueberdosis/tiptap/issues/3700
  const handleUpdate = useCallback(
    ({ editor }: EditorEvents['update']) => {
      // Compare previous/current nodes to detect deleted ones.
      const prevNodesById: Record<string, Node> = {}
      previousState.current?.doc.forEach(node => {
        const { uploadId } = node.attrs
        if (uploadId) {
          prevNodesById[uploadId] = node
        }
      })
      const nodesById: Record<string, Node> = {}
      editor.state.doc.forEach(node => {
        const { uploadId } = node.attrs
        if (uploadId) {
          nodesById[uploadId] = node
        }
      })
      previousState.current = editor.state

      Object.entries(prevNodesById).forEach(([id, node]) => {
        if (nodesById[id] === undefined) {
          onNodeRemoved?.(node)
        }
      })
    },
    [onNodeRemoved],
  )

  // editor.on is called only the first time, that's why we have to return a new function instead of using useEffect.
  // should be fixed in tiptap@2.0.3
  // https://github.com/ueberdosis/tiptap/issues/2403
  //
  // useEffect(() => {
  //   if (!editor) {
  //     return
  //   }
  //
  //   editor.on('update', handleUpdate)
  //
  //   return () => {
  //     editor.off('update', handleUpdate)
  //   }
  // }, [editor, handleUpdate])

  return { handleUpdate }
}
